import { useCallback, useMemo, useState } from "react";

import theme from "theme";
import { colors } from "theme/palette";

import Button from "components/Button";
import ScrollableTable from "components/ScrollableTable";
import Select from "components/Select";
import Stack from "components/Stack";
import Switch from "components/Switch";
import Text from "components/Text";

import { DevBudgetCalculationMethods } from "entities/AnalyticsFilterGroup/components/DevBudgetTable/constants";
import {
  exportTableAsXLSX,
  getFormatCalculatedDealsDataForCells,
  getSortedDealsRows,
  getTableHeaders,
} from "entities/AnalyticsFilterGroup/components/DevBudgetTable/utils";
import EditSelectedDealsButton from "entities/AnalyticsFilterGroup/components/EditSelectedDealsButton";
import { AnalyticsDevBudgetTabs } from "entities/AnalyticsFilterGroup/components/Tabs/constants";
import { IFilterGroup } from "entities/AnalyticsFilterGroup/sdk";
import { combineDealsForFilterGroup } from "entities/AnalyticsFilterGroup/utils";

interface IAnalyticsTable {
  deals: IFilterGroup["deals"];
  excludedDeals: IFilterGroup["excluded_deals"];
  dealSumData: IFilterGroup["deal_sum_data"];
  dealAvgData: IFilterGroup["deal_avg_data"];
  selectedTab: AnalyticsDevBudgetTabs;
  loading: boolean;
  onExcludedDealsUpdate: (dealIds: Array<number>) => void;
}

const calculationOptions = Object.values(DevBudgetCalculationMethods);

const AnalyticsTable = ({
  deals,
  excludedDeals,
  dealSumData,
  dealAvgData,
  selectedTab,
  loading,
  onExcludedDealsUpdate,
}: IAnalyticsTable) => {
  const [selectedCalculation, setSelectedCalculation] =
    useState<DevBudgetCalculationMethods>(DevBudgetCalculationMethods.total);
  const [selectedSortOption, setSelectedSortOption] = useState<
    string | undefined
  >("total_units");
  const [hideEmptyRows, setHideEmptyRows] = useState(false);

  const sortedDeals = useMemo(
    () =>
      getSortedDealsRows({
        deals,
        dealSumData,
        dealAvgData,
        hideEmptyRows,
        selectedTab,
        selectedCalculation,
        selectedSortOption,
      }),
    [
      deals,
      dealSumData,
      dealAvgData,
      hideEmptyRows,
      selectedTab,
      selectedCalculation,
      selectedSortOption,
    ]
  );

  const calculatedDealsDataWithKeys = useMemo(
    () =>
      getFormatCalculatedDealsDataForCells({
        sortedDeals,
        dealAvgData,
        selectedTab,
        selectedCalculation,
      }),
    [sortedDeals, dealAvgData, selectedTab, selectedCalculation]
  );

  const headers = useMemo(
    () =>
      getTableHeaders({ selectedTab, calculationMethod: selectedCalculation }),
    [selectedTab, selectedCalculation]
  );

  const handleCalculationChange = useCallback((event: any) => {
    setSelectedCalculation(event.target.value);
  }, []);

  const handleHideEmptyRowsSwitchChange = useCallback(
    (event: any, value: boolean) => {
      setHideEmptyRows(value);
    },
    []
  );

  const handleSetSortBy = useCallback(
    ({ key }: { key: string | undefined }) => setSelectedSortOption(key),
    []
  );

  const handleExportClick = useCallback(() => {
    exportTableAsXLSX({ headers, rows: sortedDeals, selectedTab });
  }, [headers, sortedDeals, selectedTab]);

  const allDeals = combineDealsForFilterGroup({ deals, excludedDeals });

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ marginTop: theme.spacing(2.5) }}
      >
        <EditSelectedDealsButton
          deals={allDeals}
          onExcludedDealsUpdate={onExcludedDealsUpdate}
        />
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="flex-end"
          flex={1}
        >
          <Stack direction="row" alignItems="center">
            <Text>Hide empty rows:</Text>
            <Switch onChange={handleHideEmptyRowsSwitchChange}></Switch>
          </Stack>
          <Select
            value={selectedCalculation}
            label="Calculation"
            options={calculationOptions.map((x) => ({ value: x, label: x }))}
            onChange={handleCalculationChange}
            color="primary"
            sx={{
              flex: "130px",
              flexGrow: 0,
              backgroundColor: colors.white,
              "& .MuiOutlinedInput-root": {
                height: "2.25em",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: colors.blue100,
                textTransform: "uppercase",
                fontWeight: 600,
                fontSize: "12px",
                color: colors.blue100,
              },
              "& .MuiInputLabel-root": {
                textTransform: "uppercase",
                fontWeight: 600,
                transform: "translate(14px, -5px) scale(0.75);",
                fontSize: "12px",
                color: colors.blue100,
              },
            }}
            size="small"
          />
          <Button color="primary" onClick={handleExportClick}>
            Export
          </Button>
        </Stack>
      </Stack>
      <Stack
        spacing={1}
        sx={{
          marginTop: theme.spacing(3),
        }}
      >
        <Stack direction="row" spacing={1}>
          {/* There's an "Averages line" */}
          <Text variant="text3" color={colors.gray60} fontWeight={700}>
            {calculatedDealsDataWithKeys.length - 1} deals shown
          </Text>
          {/* {excludedDeals.length > 0 && (
            <Text variant="text3" color={colors.gray60}>
              • {excludedDeals.length} deals hidden
            </Text>
          )} */}
        </Stack>
        <ScrollableTable
          dynamicHyphen
          rows={calculatedDealsDataWithKeys}
          headers={headers}
          sortBy={selectedSortOption}
          setSortBy={handleSetSortBy}
          showLoading={loading}
          sx={{
            maxHeight: "calc(100vh - 460px)",
          }}
        />
      </Stack>
    </>
  );
};

export default AnalyticsTable;
